import React, { useState } from "react";
import jsPDF from "jspdf";
import domtoimage from "dom-to-image-more";
import { waitFor } from "@testing-library/react";
import CapitalizeFirstLetter from "../../../../shared/utils/CapitalizeFirstLetter";

export default function PixellPdiFullPreview({ campaignDetails, jobData }) {
  const final = "/assests/AdminDashboard/pdifinalpage.webp";
  const pdiMap = "/assests/AdminDashboard/pdimap.png";

  const [loading, setLoading] = useState(false);
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

  const mapMarkers = Array.isArray(jobData)
    ? jobData
        .map((j) => {
          const lat = Number(
            j?.jobId?.locationCoordinates?.latitude ||
              j?.jobId?.locationCoordinates?.lat,
          );
          const lng = Number(
            j?.jobId?.locationCoordinates?.longitude ||
              j?.jobId?.locationCoordinates?.lng,
          );

          return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
        })
        .filter(Boolean)
    : [];

  const avgLat =
    mapMarkers.length > 0
      ? mapMarkers.reduce((sum, m) => sum + m.lat, 0) / mapMarkers.length
      : undefined;

  const avgLng =
    mapMarkers.length > 0
      ? mapMarkers.reduce((sum, m) => sum + m.lng, 0) / mapMarkers.length
      : undefined;

  const zoom =
    mapMarkers.length === 1 ? 15 : mapMarkers.length > 1 ? 7 : undefined;

  const staticMapUrl =
    mapMarkers.length && avgLat && avgLng
      ? "https://maps.googleapis.com/maps/api/staticmap" +
        `?size=800x350` +
        `&center=${avgLat},${avgLng}` +
        `&zoom=${zoom}` +
        `&markers=color:red|${mapMarkers
          .map((m) => `${m.lat},${m.lng}`)
          .join("|")}` +
        `&key=${GOOGLE_MAPS_API_KEY}`
      : null;

  let pageCounter = 3; // because 1 & 2 are fixed pages

  const cover = "/assests/AdminDashboard/pdicover.webp";
  const logo = "/assests/images/pixell dark.png";
  const pdiperson =
    jobData[0]?.answeredByUser?.profilePhoto ||
    "/assests/AdminDashboard/pdiperson.webp";
  // "/assests/AdminDashboard/userForIP.webp";
  const xhalflogo = "/assests/AdminDashboard/x-half-logo.webp";

  const questionsPerPage = 10;
  const totalQuestionPages = Math.ceil(jobData.length / questionsPerPage);
  const finalPageNumber = 3 + totalQuestionPages;

  const waitForImages = async (container) => {
    const images = container.querySelectorAll("img");

    await Promise.all(
      [...images].map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth !== 0) return resolve();
            img.onload = img.onerror = resolve;
          }),
      ),
    );
  };

  const forceCrossOrigin = (container) => {
    container.querySelectorAll("img").forEach((img) => {
      img.crossOrigin = "anonymous";
    });
  };

  const downloadFullPDF = async () => {
    setLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 800));

      const pdf = new jsPDF("p", "pt", "a4");

      const renderPage = async (element, addPage = false) => {
        if (!element) return;

        forceCrossOrigin(element);
        await waitForImages(element);

        if (addPage) pdf.addPage();

        const scale = 2;

        const dataUrl = await domtoimage.toJpeg(element, {
          quality: 0.95,
          width: element.offsetWidth * scale,
          height: element.offsetHeight * scale,
          useCORS: true, // 🔥 REQUIRED
          cacheBust: true, // 🔥 REQUIRED
          style: {
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          },
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const img = new Image();
        img.src = dataUrl;

        await new Promise((resolve) => {
          img.onload = () => {
            const imgHeight = (img.height * pdfWidth) / img.width;
            pdf.addImage(dataUrl, "JPEG", 0, 0, pdfWidth, imgHeight);
            resolve();
          };
        });
      };

      // ✅ PASS ELEMENTS (NOT IDs)
      const pages = document.querySelectorAll("[id^='pdi-page-']");

      for (let i = 0; i < pages.length; i++) {
        await renderPage(pages[i], i !== 0);
      }
      // await renderPage(`pdi-page-${finalPageNumber}`, true);
      const campaignName = CapitalizeFirstLetter(
        campaignDetails?.campaignTitle,
      );

      pdf.save(`PDIReport_${campaignName}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center p-2 bg-gray-100 ">
      <button
        onClick={downloadFullPDF}
        disabled={loading}
        className={`px-6 py-3 fixed top-12 z-20 rounded-lg mb-6 shadow-md ${
          loading
            ? "bg-gray-400 text-white cursor-not-allowed"
            : "bg-blue-600 text-white"
        }`}
      >
        {loading ? "Preparing PDF, please wait..." : "Download Full PDI PDF"}
      </button>

      {/* ---------------------- PAGE 1 ---------------------- */}
      <div
        id="pdi-page-1"
        style={{
          width: "794px",
          height: "1123px",
          position: "relative",
          overflow: "hidden",
          background: "#ffffff",
          border: "1px solid gray",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <img
          src={cover}
          alt="PDI Background"
          style={{
            position: "absolute",
            top: "160px",
            left: 0,
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            width: "100%",
            height: "90%",
            objectFit: "cover",
            opacity: 0.95,
            zIndex: 3,
          }}
        />

        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, rgba(243,243,243,0.85), rgba(93,173,213,0.35))",
            zIndex: 2,
          }}
        />

        <img
          src={logo}
          alt="Pixell Logo"
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            width: "160px",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            zIndex: 3,
          }}
        />

        <div
          style={{
            position: "absolute",
            top: "75px",
            left: "40px",
            width: "390px",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            zIndex: 4,
            textAlign: "left",
          }}
        >
          <h4
            style={{
              margin: 0,
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
              marginBottom: "10px",
              fontSize: "20px",
              color: "#716f6fff",
              fontWeight: 500,
            }}
          >
            Comprehensive
          </h4>

          <h1
            style={{
              fontSize: "34px",
              fontWeight: 800,
              lineHeight: "1.4",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
              marginBottom: "28px",
              color: "#000",
            }}
          >
            Pre Dispatch <br /> Inspection Report
          </h1>
          <hr
            style={{ margin: "1rem 0rem 1rem 0rem" }}
            className="border-t-2 border-gray-500"
          />
          <div
            style={{
              marginTop: "30px",

              marginBottom: "40px",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <p
              style={{
                fontSize: "20px",
                color: "#8d8787ff",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              Thorough Inspection Report
            </p>
            <div
              style={{
                fontSize: "26px",
                fontWeight: 700,
                color: "#0ea5e9",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              100+ quality checks
            </div>
          </div>

          <div
            style={{
              marginBottom: "30px",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <p
              style={{
                fontSize: "20px",
                color: "#8d8787ff",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              Trusted By
            </p>
            <div
              style={{
                fontSize: "26px",
                fontWeight: 700,
                color: "#0ea5e9",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              Over 200 brands
            </div>
          </div>
          <hr
            style={{ margin: "1rem 0rem 1rem 0rem" }}
            className="border-t-2 border-gray-500"
          />
          <p
            style={{
              fontSize: "20px",
              color: "#555",
              lineHeight: "1.6",
              width: "380px",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <span
              style={{
                fontWeight: 700,
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              World’s first and only
            </span>{" "}
            contract manufacturing tech platform for retail branding.
          </p>
        </div>
      </div>
      {/* ---------------------- PAGE 2 ---------------------- */}
      <div
        id="pdi-page-2"
        style={{
          width: "794px",
          height: "1123px",
          position: "relative",

          overflow: "hidden",
          background: "#ffffff",

          border: "1px solid gray",
          padding: "0px 20px",
        }}
      >
        <img
          src={logo}
          alt="Pixell Logo"
          style={{
            position: "absolute",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            top: "20px",
            right: "20px",
            width: "100px",
            zIndex: 3,
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            margin: "6rem 0rem 1rem 0rem",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "14px",
              justifyContent: "space-evenly",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
              alignItems: "center",
            }}
          >
            {campaignDetails?.organizationId?.brandLogo ? (
              <img
                src={campaignDetails.organizationId.brandLogo}
                alt="template"
                style={{
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
                className="w-[60px] h-[60px] object-contain"
              />
            ) : (
              <div className="w-[60px] h-[60px] border-2 border-solid  rounded-md text-xs flex items-center justify-center text-gray-500">
                No Image
              </div>
            )}

            <div
              style={{
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
                width: "400px",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                {campaignDetails?.organizationId?.name || "Not Available"}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                Campaign ID :{" "}
                {campaignDetails?.campaignSerialNumber || "Not Available"}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                {campaignDetails?.campaignTitle || "Not Available"}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                {campaignDetails?.createdAt
                  ? new Date(campaignDetails.createdAt).toLocaleDateString(
                      "en-GB",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      },
                    )
                  : "Not Available"}
              </div>
            </div>
          </div>
        </div>

        <hr
          style={{ margin: "1rem 0rem 1rem 0rem" }}
          className="border-t-2 border-gray-500"
        />

        <div
          style={{
            padding: "12px 16px",
            margin: "1rem 0rem 1rem 0rem",
          }}
          className="border-l-2 border-gray-500"
        >
          <div
            style={{
              fontSize: 14,
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            Product:
          </div>
          <div
            style={{
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: "#6b7280",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              Product:
            </span>{" "}
            <span
              style={{
                fontSize: 13,
                color: "#6b7280",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              {jobData[0]?.productType || "Not Available"}
            </span>
          </div>
          {/* <div
            style={{
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: "#6b7280",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              Dimensions:
            </span>{" "}
            <span
              style={{
                fontSize: 13,
                color: "#6b7280",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              10 ft. 8 in. × 4 ft. 2 in. × 4 in.
            </span>
          </div> */}
          {/* <div
            style={{
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: "#6b7280",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              Weight:
            </span>{" "}
            <span
              style={{
                fontSize: 13,
                color: "#6b7280",
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              20 Kg Approx
            </span>
          </div> */}
        </div>
        <hr
          style={{ margin: "1rem 0rem 1rem 0rem" }}
          className="border-t-2 border-gray-500"
        />
        <div
          style={{
            margin: "1rem 0rem 1rem 0rem",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
          }}
        >
          <h3
            style={{
              fontSize: 14,
              fontWeight: 700,
              marginBottom: 8,
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            Overall Report Summary
          </h3>

          <p
            style={{
              fontSize: 10,
              color: "gray",
              lineHeight: 1.55,
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            This Pre-Delivery Inspection (PDI) has been conducted to evaluate
            the quality, construction, and readiness of the Backlit Glow Sign
            Board fabricated by the vendor for dispatch. The inspection covered
            all key parameters essential for ensuring consistent performance,
            long-term durability, and adherence to Pixell’s manufacturing
            standards.
            <br />
            <br />
            The audit included verification of structural frame, acrylic/flex
            face quality, printing and color accuracy, LED module installation,
            power supply configuration, wiring safety, and overall finish and
            craftsmanship. Measurements and specifications were cross-checked
            against approved artwork and technical guidelines.
          </p>
        </div>
        <div
          style={{
            margin: "1rem 0rem 1rem 0rem",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
          }}
        >
          <h3
            style={{
              fontSize: 14,
              fontWeight: 700,
              marginBottom: 8,
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            Regional Touch points
          </h3>
          <div
            style={{
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            {/* <img
              style={{
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
              src={pdiMap}
              alt="Pdi Map"
              height={100}
              width={500}
            /> */}
            <img
              src={staticMapUrl}
              alt="Regional Touch Points"
              crossOrigin="anonymous"
              style={{
                width: "100%",
                height: "220px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </div>
          <div
            className="mt-6 w-full"
            style={{
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <table
              className="w-full border-collapse"
              style={{
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              <thead
                style={{
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                <tr
                  className="bg-gray-100 text-left font-semibold text-gray-600"
                  style={{
                    fontSize: 6,
                    border: "none", // ✅ Add this
                    outline: "none", // ✅ Add this
                    boxShadow: "none",
                  }}
                >
                  <th
                    className="p-3"
                    style={{
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                    }}
                  >
                    Dealer Name
                  </th>
                  <th
                    className="p-3"
                    style={{
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                    }}
                  >
                    Product
                  </th>
                  <th
                    className="p-3"
                    style={{
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                    }}
                  >
                    City
                  </th>
                  <th
                    className="p-3"
                    style={{
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                    }}
                  >
                    State
                  </th>
                  <th
                    className="p-3 "
                    style={{
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                    }}
                  >
                    PDI Status
                  </th>
                </tr>
              </thead>

              <tbody
                className="text-gray-700"
                style={{
                  fontSize: 8,
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                {jobData.map((row, index) => (
                  <tr
                    key={index}
                    className="border-b"
                    style={{
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                    }}
                  >
                    <td
                      className="p-3"
                      style={{
                        border: "none", // ✅ Add this
                        outline: "none", // ✅ Add this
                        boxShadow: "none",
                      }}
                    >
                      {row?.jobId?.storeName || "Not Available"}
                    </td>
                    <td
                      className="p-3"
                      style={{
                        border: "none", // ✅ Add this
                        outline: "none", // ✅ Add this
                        boxShadow: "none",
                      }}
                    >
                      {row?.productType || "Not Available"}
                    </td>
                    <td
                      className="p-3"
                      style={{
                        border: "none", // ✅ Add this
                        outline: "none", // ✅ Add this
                        boxShadow: "none",
                      }}
                    >
                      {row?.jobId?.city || "Not Available"}
                    </td>
                    <td
                      className="p-3"
                      style={{
                        border: "none", // ✅ Add this
                        outline: "none", // ✅ Add this
                        boxShadow: "none",
                      }}
                    >
                      {row?.jobId?.state || "Not Available"}
                    </td>

                    <td
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        border: "none", // ✅ Add this
                        outline: "none", // ✅ Add this
                        boxShadow: "none",
                      }}
                    >
                      {row?.jobId?.batchOfJobsForVendorId?.pdiStatus ===
                      "Approved" ? (
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            background: "#6ee7b7",
                            border: "none", // ✅ Add this
                            outline: "none", // ✅ Add this
                            boxShadow: "none",
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <span
                            style={{
                              color: "white",
                              fontSize: "12px",
                              border: "none", // ✅ Add this
                              outline: "none", // ✅ Add this
                              boxShadow: "none",
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              lineHeight: 1,
                            }}
                          >
                            ✔
                          </span>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            border: "none", // ✅ Add this
                            outline: "none", // ✅ Add this
                            boxShadow: "none",
                            background: "#d1d5db",
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <span
                            style={{
                              color: "white",
                              fontSize: "12px",
                              border: "none", // ✅ Add this
                              outline: "none", // ✅ Add this
                              boxShadow: "none",
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              lineHeight: 1,
                            }}
                          >
                            ✔
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {jobData.flatMap((ele, jobIndex) =>
        Object.entries(ele?.pdiAnswer || {}).flatMap(
          (category, categoryIndex) => {
            const [categoryName, questions] = category;
            const totalPages = Math.ceil(questions.length / questionsPerPage);

            return Array.from({ length: totalPages }, (_, pageIndex) => {
              const start = pageIndex * questionsPerPage;
              const end = start + questionsPerPage;
              const questionsForPage = questions.slice(start, end);

              const pageId = `pdi-page-${pageCounter++}`;

              return (
                <div
                  key={`${jobIndex}-${categoryIndex}-${pageIndex}`}
                  id={pageId}
                  style={{
                    width: "794px",
                    height: "1123px",
                    background: "#fff",
                    border: "1px solid gray",
                    padding: "0px 20px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <img
                    src={logo}
                    alt="Pixell Logo"
                    style={{
                      position: "absolute",
                      top: "20px",
                      right: "20px",
                      width: "100px",
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      zIndex: 3,
                    }}
                  />
                  <img
                    src={xhalflogo}
                    alt="PDI pixell logo"
                    style={{
                      position: "absolute",
                      top: "200px",
                      left: "200px",
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      width: "100%",
                      height: "70%",
                      objectFit: "contain",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "6rem 0rem 1rem 0rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "14px",
                        border: "none", // ✅ Add this
                        outline: "none", // ✅ Add this
                        boxShadow: "none",
                      }}
                    >
                      <div
                        style={{
                          border: "none", // ✅ Add this
                          outline: "none", // ✅ Add this
                          boxShadow: "none",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 12,
                            border: "none", // ✅ Add this
                            outline: "none", // ✅ Add this
                            boxShadow: "none",
                          }}
                        >
                          Dealer Name: {ele?.jobId?.storeName}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#6b7280",
                            border: "none", // ✅ Add this
                            outline: "none", // ✅ Add this
                            boxShadow: "none",
                          }}
                        >
                          Campaign ID :{" "}
                          {campaignDetails?.campaignSerialNumber ||
                            "Not Available"}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#6b7280",
                            border: "none", // ✅ Add this
                            outline: "none", // ✅ Add this
                            boxShadow: "none",
                          }}
                        >
                          {campaignDetails?.campaignTitle ||
                            "Not Available"}{" "}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        textAlign: "left",
                        border: "none", // ✅ Add this
                        outline: "none", // ✅ Add this
                        boxShadow: "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          border: "none", // ✅ Add this
                          outline: "none", // ✅ Add this
                          boxShadow: "none",
                        }}
                      >
                        Inspection Expert
                      </div>
                      <div
                        className="flex items-center justify-center gap-2"
                        style={{
                          border: "none", // ✅ Add this
                          outline: "none", // ✅ Add this
                          boxShadow: "none",
                        }}
                      >
                        <img
                          src={pdiperson}
                          alt="Expert"
                          style={{
                            width: 44,
                            border: "none", // ✅ Add this
                            outline: "none", // ✅ Add this
                            boxShadow: "none",
                            height: 44,
                            borderRadius: "50%",
                            marginTop: 8,
                          }}
                        />
                        <div
                          style={{
                            border: "none", // ✅ Add this
                            outline: "none", // ✅ Add this
                            boxShadow: "none",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              marginTop: 4,
                              border: "none", // ✅ Add this
                              outline: "none", // ✅ Add this
                              boxShadow: "none",
                            }}
                          >
                            {ele?.answeredByUser?.name || "Not Available"}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#6b7280",
                              border: "none", // ✅ Add this
                              outline: "none", // ✅ Add this
                              boxShadow: "none",
                            }}
                          >
                            Subject Matter Expert.
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#6b7280",
                              border: "none", // ✅ Add this
                              outline: "none", // ✅ Add this
                              boxShadow: "none",
                            }}
                          >
                            {ele?.answeredByUser?.experience || 0} years exp.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr
                    style={{ margin: "1rem 0rem 1rem 0rem" }}
                    className="border-t-2 border-gray-500"
                  />

                  {/* Category Heading */}
                  <h2
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      marginBottom: "15px",
                      color: "#000",
                    }}
                  >
                    {categoryName}
                  </h2>

                  {/* Questions Container */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflowY: "auto",
                    }}
                  >
                    {questionsForPage.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "10px",
                          borderRadius: "6px",
                          marginBottom: "8px",
                          fontSize: "10px",
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                          Q{start + index + 1}. {item.question}
                        </div>
                        <div style={{ color: "#374151", marginBottom: "4px" }}>
                          Ans. {item.answer || "Not Available"}
                        </div>
                        {item.imageURL && (
                          <img
                            src={item.imageURL}
                            alt="question-img"
                            style={{
                              marginTop: "6px",
                              width: "80px",
                              height: "80px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          },
        ),
      )}

      <div
        id={`pdi-page-${finalPageNumber}`}
        style={{
          width: "794px",
          height: "1123px",
          background: "#ffffff",
          border: "1px solid gray",
          // marginTop: "20px",
          padding: "0px 20px",
          position: "relative",
        }}
      >
        <img
          src={logo}
          alt="Pixell Logo"
          style={{
            position: "absolute",
            top: "20px",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            right: "20px",
            width: "100px",
            zIndex: 3,
          }}
        />
        <img
          src={xhalflogo}
          alt="PDI pixell logo"
          style={{
            position: "absolute",
            top: "200px",
            left: "200px",
            border: "none",
            outline: "none",
            boxShadow: "none",
            width: "100%",
            height: "70%",
            objectFit: "contain",
            zIndex: 1, // ⭐ lower than table
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            display: "flex",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            justifyContent: "space-between",
            margin: "6rem 0rem 1rem 0rem",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "14px",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <div
              style={{
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                Organization Name:{" "}
                {campaignDetails?.organizationId?.name || "Not Available"}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                Campaign ID :{" "}
                {campaignDetails?.campaignSerialNumber || "Not Available"}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                {campaignDetails?.campaignTitle || "Not Available"}
              </div>
            </div>
          </div>

          <div
            style={{
              textAlign: "left",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              Inspection Expert
            </div>
            <div
              className="flex items-center justify-center gap-2"
              style={{
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              <img
                src={pdiperson}
                alt="Expert"
                style={{
                  width: 44,
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                  height: 44,
                  borderRadius: "50%",
                  marginTop: 8,
                }}
              />
              <div
                style={{
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    marginTop: 4,
                    border: "none", // ✅ Add this
                    outline: "none", // ✅ Add this
                    boxShadow: "none",
                  }}
                >
                  {jobData[0]?.answeredByUser?.name || "Not Available"}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "#6b7280",
                    border: "none", // ✅ Add this
                    outline: "none", // ✅ Add this
                    boxShadow: "none",
                  }}
                >
                  Subject Matter Expert.
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "#6b7280",
                    border: "none", // ✅ Add this
                    outline: "none", // ✅ Add this
                    boxShadow: "none",
                  }}
                >
                  {jobData[0]?.answeredByUser?.experience || 0} years exp.
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr
          style={{ margin: "1rem 0rem 1rem 0rem" }}
          className="border-t-2 border-gray-500"
        />

        <h2
          style={{
            fontSize: 14,
            fontWeight: 700,
            marginTop: 12,
            marginBottom: 24,
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
          }}
        >
          Overall Assessment
        </h2>

        <table
          style={{
            width: "100%",
            fontSize: 8,
            borderCollapse: "collapse",
            border: "none", // ✅ Add this
            outline: "none", // ✅ Add this
            boxShadow: "none",
            position: "relative", // ⭐ required
            zIndex: 10,
          }}
        >
          <thead
            style={{
              background: "white",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
              zIndex: 3,
            }}
          >
            <tr
              style={{
                color: "#6b7280",
                fontWeight: 600,
                border: "none", // ✅ Add this
                outline: "none", // ✅ Add this
                boxShadow: "none",
              }}
            >
              <th
                style={{
                  padding: "10px 4px",
                  textAlign: "left",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                Parameter
              </th>
              <th
                style={{
                  padding: "10px 4px",
                  textAlign: "center",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                Pass
              </th>
              <th
                style={{
                  padding: "10px 4px",
                  textAlign: "center",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                Fail
              </th>
              <th
                style={{
                  padding: "10px 4px",
                  textAlign: "center",
                  border: "none", // ✅ Add this
                  outline: "none", // ✅ Add this
                  boxShadow: "none",
                }}
              >
                Improvement
              </th>
            </tr>
          </thead>

          <tbody
            className="text-gray-700"
            style={{
              fontSize: 10,
              border: "none",
              outline: "none",
              boxShadow: "none",
            }}
          >
            {[
              "Structure",
              "MS Frame",
              "Electricals",
              "Lighting & Wiring",
              "Printing Quality",
              "Printing Substrate",
              "Fabrication",
              "Overall Finesse",
            ].map((row, idx) => (
              <tr
                key={idx}
                style={{
                  background: idx % 2 === 1 ? "#ffffff" : "#f9fafb", // ⭐ even row color

                  borderBottom: "1px solid #f0f0f0",
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                }}
              >
                <td
                  style={{
                    padding: "20px 4px",
                    border: "none",
                  }}
                >
                  {row}
                </td>

                <td
                  style={{
                    textAlign: "center",
                    border: "none",
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                      background: "#4CAF50",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#fff",
                      fontSize: 10,
                      margin: "0 auto",
                    }}
                  >
                    ✓
                  </div>
                </td>

                <td
                  style={{
                    textAlign: "center",
                    border: "none",
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: "#e5e7eb",
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#9ca3af",
                      fontSize: 10,
                      margin: "0 auto",
                    }}
                  >
                    ✕
                  </div>
                </td>

                <td
                  style={{
                    textAlign: "center",
                    border: "none",
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: "#e5e7eb",
                      border: "none", // ✅ Add this
                      outline: "none", // ✅ Add this
                      boxShadow: "none",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#9ca3af",
                      fontSize: 10,
                      margin: "0 auto",
                    }}
                  >
                    ⚠
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          style={{
            width: "100%",
            background: "linear-gradient(360deg, #1E88E5, #42A5F5)",
            height: "150px",
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0 40px",
            position: "relative",
            overflow: "visible",
            top: "8rem",
            margin: "0px 70px 0px 0px",
            boxShadow: "0px 3px 11px #40a3f4",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: "250px",
              fontSize: "24px",
              fontWeight: 600,
              lineHeight: "1.4",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
              color: "white",
              whiteSpace: "pre-line", // ⭐ PDF-safe line breaks
            }}
          >
            {"Be sure on quality\nwith Pixell"}
          </div>

          <img
            src={final}
            alt="Pixell Expert"
            style={{
              position: "absolute",
              left: "30px",
              bottom: "-12px",
              border: "none", // ✅ Add this
              outline: "none", // ✅ Add this
              boxShadow: "none",
              height: "240px",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        </div>
      </div>
    </div>
  );
}
